class RsSizeConfirmationDrawer extends SideDrawer {
  connectedCallback() {
    this._handleCartAdd = this._handleCartAdd.bind(this);
    this._handleFormSubmit = this._handleFormSubmit.bind(this);
    this._handleConfirm = this._handleConfirm.bind(this);

    document.addEventListener('on:cart:add', this._handleCartAdd);
    document.addEventListener('submit', this._handleFormSubmit, true);
    this.addEventListener('click', (evt) => {
      if (evt.target.closest('.js-scd-confirm')) this._handleConfirm();
      if (evt.target.closest('.js-scd-size-guide')) this._showSizeGuide();
      if (evt.target.closest('.js-scd-back')) this._hideSizeGuide();
    });
    this.addEventListener('change', (evt) => {
      if (evt.target.name === 'scd-choice') this._clearError();
    });

    requestAnimationFrame(() => this._patchCartDrawer());
  }

  disconnectedCallback() {
    document.removeEventListener('on:cart:add', this._handleCartAdd);
    document.removeEventListener('submit', this._handleFormSubmit, true);
  }

  _patchCartDrawer() {
    const cartDrawer = document.querySelector('.js-cart-drawer');
    if (!cartDrawer || cartDrawer._scdPatched) return;
    cartDrawer._scdPatched = true;
    const originalOpen = cartDrawer.open.bind(cartDrawer);
    cartDrawer.open = (...args) => {
      if (this._blockCartOpen) return;
      return originalOpen(...args);
    };
  }

  async _handleFormSubmit(evt) {
    this._blockCartOpen = false;
    this._preAddDistinctVariantCount = 0;

    const form = evt.target;
    if (!form.getAttribute('action')?.endsWith('/cart/add')) return;

    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput) return;
    const addedVariantId = Number(variantInput.value);
    if (!addedVariantId) return;

    let picker = null;
    try {
      picker = form
        .closest(':has(variant-picker[data-product-id])')
        ?.querySelector('variant-picker[data-product-id]');
    } catch {}
    picker = picker || document.querySelector('variant-picker[data-product-id]');
    const productId = picker ? Number(picker.dataset.productId) : null;
    if (!productId) return;

    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      const productItems = cart.items.filter((i) => i.product_id === productId);
      const hasOtherVariant = productItems.some((i) => i.variant_id !== addedVariantId);
      if (hasOtherVariant) {
        this._blockCartOpen = true;
        this._preAddDistinctVariantCount = new Set(productItems.map((i) => i.variant_id)).size;
      }
    } catch {}
  }

  async _handleCartAdd(evt) {
    const addedVariantId = evt.detail.variantId;
    const wasBlocked = this._blockCartOpen;
    const preAddDistinctVariantCount = this._preAddDistinctVariantCount || 0;
    this._blockCartOpen = false;
    this._preAddDistinctVariantCount = 0;

    const openCart = () => {
      const cartDrawer = document.querySelector('.js-cart-drawer');
      if (cartDrawer && !cartDrawer.hasAttribute('open')) {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
      }
    };

    let cart;
    try {
      const res = await fetch('/cart.js');
      cart = await res.json();
    } catch {
      if (wasBlocked) openCart();
      return;
    }

    const addedItem = cart.items.find((i) => i.variant_id === addedVariantId);
    if (!addedItem) {
      if (wasBlocked) openCart();
      return;
    }

    this._existingItems = cart.items.filter((i) => i.product_id === addedItem.product_id && i.variant_id !== addedVariantId);
    this._addedItem = addedItem;

    if (this._existingItems.length === 0) {
      if (wasBlocked) openCart();
      return;
    }
    if (this._existingItems.length > 1 || preAddDistinctVariantCount > 1) {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
      return;
    }

    this._closeOtherDrawers();
    this._renderOptions(this._existingItems[0], this._addedItem);
    this.open();
  }

  _clearError() {
    const errorEl = this.querySelector('.rs-scd__error');
    if (errorEl) errorEl.textContent = '';
  }

  async _handleConfirm() {
    const selected = this.querySelector('input[name="scd-choice"]:checked');
    if (!selected) {
      const errorEl = this.querySelector('.rs-scd__error');
      if (errorEl) errorEl.textContent = errorEl.dataset.message || 'Please select one of the choices above';
      return;
    }
    this._clearError();

    if (selected.value === 'keep-both') {
      this.close();
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
      return;
    }

    const confirmBtn = this.querySelector('.js-scd-confirm');
    if (confirmBtn) confirmBtn.setAttribute('disabled', '');

    const itemsToRemove =
      selected.value === 'keep-existing' ? [this._addedItem] : this._existingItems;

    try {
      for (const item of itemsToRemove) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
      }
    } catch {
      if (confirmBtn) confirmBtn.removeAttribute('disabled');
      const errorEl = this.querySelector('.rs-scd__error');
      if (errorEl) errorEl.textContent = 'Something went wrong. Please try again.';
      return;
    }

    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('on:cart:after-merge', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
    this.close();
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));

    if (confirmBtn) confirmBtn.removeAttribute('disabled');
  }

  _closeOtherDrawers() {
    document.querySelectorAll('.drawer[open]').forEach((el) => {
      if (el !== this && typeof el.close === 'function') el.close();
    });
    document.querySelectorAll('modal-dialog[open]').forEach((el) => {
      if (typeof el.close === 'function') el.close();
    });
  }

  _findSizeGuide(root, productId) {
    return root.querySelector('size-guide') || (productId && root.getElementById(`size-chart-${productId}`));
  }

  _renderOptions(existingItem, addedItem) {
    const opener = this.querySelector('.rs-scd__size-guide-opener');
    if (opener) {
      const inDom = !!this._findSizeGuide(document, addedItem.product_id);
      opener.hidden = !inDom;
      if (!inDom && addedItem.url) this._revealSizeGuideIfAvailable(addedItem.url, addedItem.product_id, opener);
    }

    const container = this.querySelector('.rs-scd__options');
    if (!container) return;

    const prefix = container.dataset.keepSizePrefix || 'Keep only the size:';
    const bothLabel = container.dataset.bothSizesLabel || 'Continue with both sizes';

    container.innerHTML = `
      <label class="rs-scd__option">
        <input type="radio" name="scd-choice" value="keep-existing">
        <span class="rs-scd__option-label">${this._escape(prefix)} <strong>${this._escape(this._variantSize(existingItem))}</strong></span>
      </label>
      <label class="rs-scd__option">
        <input type="radio" name="scd-choice" value="keep-new">
        <span class="rs-scd__option-label">${this._escape(prefix)} <strong>${this._escape(this._variantSize(addedItem))}</strong></span>
      </label>
      <label class="rs-scd__option">
        <input type="radio" name="scd-choice" value="keep-both">
        <span class="rs-scd__option-label">${this._escape(bothLabel)}</span>
      </label>
    `;
  }

  async _revealSizeGuideIfAvailable(productUrl, productId, opener) {
    try {
      const html = await theme.fetchCache.fetch(productUrl.split('?')[0]);
      if (this._findSizeGuide(new DOMParser().parseFromString(html, 'text/html'), productId)) opener.hidden = false;
    } catch {}
  }

  async _showSizeGuide() {
    const productId = this._addedItem?.product_id;
    let sizeGuide = this._findSizeGuide(document, productId);

    if (!sizeGuide && this._addedItem?.url) {
      try {
        const html = await theme.fetchCache.fetch(this._addedItem.url.split('?')[0]);
        sizeGuide = this._findSizeGuide(new DOMParser().parseFromString(html, 'text/html'), productId);
      } catch {}
    }

    if (!sizeGuide) return;

    const body = this.querySelector('.rs-scd__body');
    if (!body) return;
    const footer = this.querySelector('.rs-scd__footer');
    const heading = this.querySelector('.rs-scd__heading');
    const backBtn = this.querySelector('.rs-scd__back');

    this._savedBodyHTML = body.innerHTML;

    const content = sizeGuide.querySelector('.modal__content') || sizeGuide.querySelector('.size-guide__content');
    const titleText = sizeGuide.querySelector('.modal__edge-title')?.textContent.trim()
      || sizeGuide.getAttribute('aria-label')
      || 'Size guide';

    if (heading) {
      this._savedHeadingText = heading.textContent;
      heading.textContent = titleText;
    }
    if (backBtn) backBtn.hidden = false;

    body.innerHTML = '';
    if (content) {
      const contentClone = content.cloneNode(true);
      contentClone.className = 'rs-scd__size-guide-content';
      body.appendChild(contentClone);
    }

    if (footer) footer.hidden = true;
  }

  _hideSizeGuide() {
    if (!this._savedBodyHTML) return;
    const body = this.querySelector('.rs-scd__body');
    const footer = this.querySelector('.rs-scd__footer');
    const heading = this.querySelector('.rs-scd__heading');
    const backBtn = this.querySelector('.rs-scd__back');

    body.innerHTML = this._savedBodyHTML;
    this._savedBodyHTML = null;

    if (heading && this._savedHeadingText != null) {
      heading.textContent = this._savedHeadingText;
      this._savedHeadingText = null;
    }
    if (backBtn) backBtn.hidden = true;
    if (footer) footer.hidden = false;
  }

  _variantSize(item) {
    return item.variant_title && item.variant_title !== 'Default Title'
      ? item.variant_title
      : item.product_title;
  }

  _escape(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

customElements.define('rs-size-confirmation-drawer', RsSizeConfirmationDrawer);
